import type { LatestTestCommand, LatestTestSignal } from "@visioni/a3s-monitoring/app";
import { deliveryStatus, type TestSignalLogTone } from "./log-rows";

/** One line of the summary: "Periodic: 7 Oct, 09:12 · Delivered to Vision Patriot". */
export interface TestSignalSummaryRow {
  readonly key: string;
  /** "Periodic", "Manual" or "Last command". */
  readonly label: string;
  /** When the newest signal of this kind occurred, or `null` when none arrived in the window. */
  readonly occurredAt: string | null;
  /** What happened to it; `null` when there is no signal to judge. */
  readonly verdict: string | null;
  readonly tone: TestSignalLogTone;
}

const TRIGGER_LABELS = { periodic: "Periodic", manual: "Manual" } as const;

/**
 * Project one summary cell onto its line.
 *
 * A missing periodic signal is negative: the heartbeat should arrive on its own,
 * so its absence is the thing worth noticing. A missing manual signal is
 * neutral — it only means nobody has asked for one.
 */
export function toSummaryRow(cell: LatestTestSignal): TestSignalSummaryRow {
  const key = `${cell.signalPath}#${cell.triggerType}`;
  const label = TRIGGER_LABELS[cell.triggerType];

  if (cell.signal === null) {
    return {
      key,
      label,
      occurredAt: null,
      verdict: null,
      tone: cell.triggerType === "periodic" ? "negative" : "neutral",
    };
  }

  const { text, tone } = deliveryStatus(cell.signal.outcome, cell.signal.provider);
  return { key, label, occurredAt: cell.signal.occurredAt, verdict: text, tone };
}

/**
 * Project a command cell onto its line: when the button was last pressed for
 * this leg, and by whom.
 *
 * Neutral whether or not there is one — a command is not evidence of anything
 * arriving. Its use is beside the manual line above it: a command newer than
 * the last manual signal says the leg did not answer.
 */
export function toCommandSummaryRow(cell: LatestTestCommand): TestSignalSummaryRow {
  const key = `${cell.signalPath}#command`;
  const label = "Last command";

  if (cell.command === null) return { key, label, occurredAt: null, verdict: null, tone: "neutral" };

  // The raw `UserId` when the server could not resolve an address, as in the log.
  const sender = cell.command.actorEmail ?? cell.command.actor;
  return { key, label, occurredAt: cell.command.occurredAt, verdict: `Sent by ${sender}`, tone: "neutral" };
}
